import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, CircularProgress, IconButton, InputAdornment, Link, TextField, Typography } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import LockResetOutlinedIcon from "@mui/icons-material/LockResetOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { appColor } from "../../theme/appColor";
import Logo from "../../Images/logo_main_black.webp";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../Store/hooks";
import { useNotifier } from "../../Core/Notifier";
import {
  useLoginMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
} from "../../Api/attoDeskApi";
import { useFormik } from "formik";
import { IAuth } from "../../Api/Interface/api.interface";
import { setEnableAuth } from "../../Store/Auth/AuthSlice";
import useBranding from "../../Core/useBranding";

type Mode = "login" | "forgot" | "reset";

const Card: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Settings → Business Profile → Login Logo, falling back to Main Logo and
  // then to the logo shipped with the app. Never the invoice Company logo.
  const branding = useBranding();

  return (
    <Box
      sx={{
        backgroundColor: appColor.white,
        boxShadow: 3,
        p: 5,
        maxWidth: 500,
        height: "auto",
        mx: "auto",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "center", pb: 4 }}>
        <img
          src={branding.login_logo_url || Logo}
          alt={branding.business_name || "TJ Bus"}
          style={{ width: "70%", height: "auto", display: "block" }}
          onError={(e) => { (e.currentTarget as HTMLImageElement).src = Logo; }}
        />
      </Box>
      {children}
    </Box>
  );
};

const readResetParams = () => {
  try {
    const q = new URLSearchParams(window.location.search);
    const token = q.get("token") || "";
    const email = q.get("email") || "";
    const isReset = window.location.pathname.includes("/reset-password") && !!token && !!email;
    return { isReset, token, email };
  } catch {
    return { isReset: false, token: "", email: "" };
  }
};

const Login: React.FC = () => {
  const dispatch = useAppDispatch();
  const { showErrorMessage, showMessage } = useNotifier();
  const [login, { isLoading }] = useLoginMutation();
  const [forgotPassword, { isLoading: sendingReset }] = useForgotPasswordMutation();
  const [resetPassword, { isLoading: resettingPw }] = useResetPasswordMutation();
  const navigate = useNavigate();

  const initialReset = useMemo(readResetParams, []);
  const [mode, setMode] = useState<Mode>(initialReset.isReset ? "reset" : "login");
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  useEffect(() => {
    if (initialReset.isReset) setMode("reset");
  }, [initialReset.isReset]);

  const loginForm = useFormik({
    initialValues: { username: "", password: "" },
    onSubmit: async (values) => {
      try {
        const loginResponse = await login(values).unwrap();
        if (!loginResponse.status) {
          showErrorMessage(loginResponse.message);
        } else {
          const tokenData = loginResponse.data as IAuth;
          localStorage.setItem("token", tokenData.token);
          dispatch(setEnableAuth());
          navigate('/dashboard/list');
        }
      } catch (error) {
        showErrorMessage("Please check you username or password");
      }
    },
  });

  const forgotForm = useFormik({
    initialValues: { email: "" },
    onSubmit: async (values) => {
      try {
        const res: any = await forgotPassword({ email: values.email.trim() }).unwrap();
        setForgotSent(true);
        showMessage(res?.message || "If that email is registered, a reset link has been sent.", "success");
      } catch (err: any) {
        showErrorMessage(err?.data?.message || "Failed to send reset email");
      }
    },
  });

  const resetForm = useFormik({
    initialValues: {
      email: initialReset.email,
      token: initialReset.token,
      password: "",
      password_confirmation: "",
    },
    onSubmit: async (values) => {
      if (values.password.length < 6) {
        showErrorMessage("Password must be at least 6 characters.");
        return;
      }
      if (values.password !== values.password_confirmation) {
        showErrorMessage("Passwords do not match.");
        return;
      }
      try {
        const res: any = await resetPassword(values).unwrap();
        showMessage(res?.message || "Password reset successfully.", "success");
        window.history.replaceState({}, document.title, "/");
        setMode("login");
      } catch (err: any) {
        showErrorMessage(err?.data?.message || "Failed to reset password");
      }
    },
  });

  const loginValid = !!loginForm.values.username && !!loginForm.values.password;
  const forgotValid = /^\S+@\S+\.\S+$/.test(forgotForm.values.email.trim());
  const resetValid =
    !!resetForm.values.email &&
    !!resetForm.values.token &&
    resetForm.values.password.length >= 6 &&
    resetForm.values.password === resetForm.values.password_confirmation;

  if (mode === "forgot") {
    return (
      <form onSubmit={forgotForm.handleSubmit}>
        <Card>
          <Typography fontWeight={700} fontSize={18} mb={0.5}>Forgot password</Typography>
          <Typography fontSize={13} color="#64748b" mb={2}>
            Enter your email and we'll send you a link to reset your password.
          </Typography>

          {forgotSent ? (
            <Box sx={{
              p: 2, mb: 2, borderRadius: 1,
              bgcolor: "#ecfdf5", border: "1px solid #a7f3d0", color: "#065f46", fontSize: 13,
            }}>
              If an account exists for <strong>{forgotForm.values.email}</strong>, a reset link
              has been sent. Please check your inbox (and spam folder).
            </Box>
          ) : (
            <TextField
              fullWidth
              variant="filled"
              label="Email"
              margin="normal"
              type="email"
              sx={{ backgroundColor: "#FFFFFF", border: "1px solid #CCCCCC" }}
              {...forgotForm.getFieldProps("email")}
            />
          )}

          <Box sx={{ display: "flex", gap: 1, mt: 2, alignItems: "center" }}>
            {!forgotSent ? (
              <Button
                variant="contained"
                type="submit"
                disabled={!forgotValid || sendingReset}
                sx={{
                  backgroundColor: appColor.black, color: "white", borderRadius: 0,
                  "&:hover": { backgroundColor: appColor.black },
                }}
              >
                {sendingReset && (
                  <Box sx={{ pr: 2, pt: 0.5 }}>
                    <CircularProgress size={16} sx={{ color: "white" }} />
                  </Box>
                )}
                Send reset link
              </Button>
            ) : null}
            <Link
              component="button"
              type="button"
              underline="hover"
              onClick={() => { setMode("login"); setForgotSent(false); }}
              sx={{ fontSize: 13, color: "#2f3891", ml: "auto" }}
            >
              Back to login
            </Link>
          </Box>
        </Card>
      </form>
    );
  }

  if (mode === "reset") {
    return (
      <form onSubmit={resetForm.handleSubmit}>
        <Card>
          <Typography fontWeight={700} fontSize={18} mb={0.5}>Reset password</Typography>
          <Typography fontSize={13} color="#64748b" mb={2}>
            Choose a new password for <strong>{resetForm.values.email || "your account"}</strong>.
          </Typography>

          <TextField
            fullWidth
            variant="filled"
            label="New password"
            margin="normal"
            type={showNewPw ? "text" : "password"}
            sx={{ backgroundColor: "#FFFFFF", border: "1px solid #CCCCCC" }}
            {...resetForm.getFieldProps("password")}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowNewPw((v) => !v)} edge="end" size="small">
                    {showNewPw ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            variant="filled"
            label="Confirm new password"
            margin="normal"
            type={showNewPw ? "text" : "password"}
            sx={{ backgroundColor: "#FFFFFF", border: "1px solid #CCCCCC" }}
            {...resetForm.getFieldProps("password_confirmation")}
          />

          <Box sx={{ display: "flex", gap: 1, mt: 2, alignItems: "center" }}>
            <Button
              variant="contained"
              type="submit"
              disabled={!resetValid || resettingPw}
              sx={{
                backgroundColor: appColor.black, color: "white", borderRadius: 0,
                "&:hover": { backgroundColor: appColor.black },
              }}
            >
              {resettingPw && (
                <Box sx={{ pr: 2, pt: 0.5 }}>
                  <CircularProgress size={16} sx={{ color: "white" }} />
                </Box>
              )}
              Reset password
            </Button>
            <Link
              component="button"
              type="button"
              underline="hover"
              onClick={() => { window.history.replaceState({}, document.title, "/"); setMode("login"); }}
              sx={{ fontSize: 13, color: "#2f3891", ml: "auto" }}
            >
              Back to login
            </Link>
          </Box>
        </Card>
      </form>
    );
  }

  return (
    <form onSubmit={loginForm.handleSubmit}>
      <Card>
        <TextField
          fullWidth
          variant="filled"
          label="Username"
          margin="normal"
          sx={{ backgroundColor: "#FFFFFF", border: "1px solid #CCCCCC" }}
          {...loginForm.getFieldProps('username')}
        />
        <TextField
          fullWidth
          variant="filled"
          label="Password"
          type={showPassword ? "text" : "password"}
          margin="normal"
          sx={{ backgroundColor: "#FFFFFF", border: "1px solid #CCCCCC" }}
          {...loginForm.getFieldProps('password')}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowPassword((v) => !v)}
                  edge="end"
                  size="small"
                >
                  {showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5, mb: 0.5 }}>
          <Box
            role="button"
            tabIndex={0}
            aria-label="Forgot password"
            onClick={() => setMode("forgot")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setMode("forgot");
              }
            }}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.5,
              py: 0.75,
              borderRadius: 999,
              cursor: "pointer",
              userSelect: "none",
              fontSize: 12.5,
              fontWeight: 600,
              letterSpacing: 0.2,
              color: "#2f3891",
              bgcolor: "rgba(47, 56, 145, 0.06)",
              border: "1px solid rgba(47, 56, 145, 0.15)",
              transition: "background-color 180ms ease, color 180ms ease, border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease",
              outline: "none",
              "& .fp-arrow": {
                transition: "transform 180ms ease, opacity 180ms ease",
                opacity: 0.7,
              },
              "&:hover": {
                bgcolor: "rgba(47, 56, 145, 0.1)",
                borderColor: "rgba(47, 56, 145, 0.35)",
                color: "#1f2a6e",
                "& .fp-arrow": { transform: "translateX(3px)", opacity: 1 },
              },
              "&:active": {
                transform: "translateY(1px)",
                bgcolor: "rgba(47, 56, 145, 0.14)",
              },
              "&:focus-visible": {
                boxShadow: "0 0 0 3px rgba(47, 56, 145, 0.25)",
                borderColor: "#2f3891",
              },
            }}
          >
            <LockResetOutlinedIcon sx={{ fontSize: 16 }} />
            <Box component="span">Forgot password?</Box>
            <ArrowForwardIcon className="fp-arrow" sx={{ fontSize: 14 }} />
          </Box>
        </Box>
        <Button
          variant="contained"
          disabled={!loginValid || isLoading}
          sx={{
            backgroundColor: appColor.black, color: "white", mt: 2,
            textAlign: "left", borderRadius: 0, width: "auto",
            "&:hover": { backgroundColor: appColor.black },
            "&:active": { backgroundColor: appColor.black },
          }}
          type="submit"
        >
          {isLoading && (
            <Box sx={{ pr: 2, pt: 0.5 }}>
              <CircularProgress size={16} sx={{ color: "white" }} />
            </Box>
          )}
          Login
        </Button>
      </Card>
    </form>
  );
};

export default React.memo(Login);
